import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, HardHat, MapPin, CalendarClock, Users, ArrowRight, CheckCircle2, ShieldCheck, Search,
} from "lucide-react";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const daysLeft = (due) => {
  if (!due) return null;
  const d = Math.ceil((new Date(`${String(due).slice(0, 10)}T23:59:59`) - new Date()) / 86400000);
  return Number.isFinite(d) ? d : null;
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-sand-50">
    <div className="border-b border-sand-200 bg-white">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-6 py-4">
        <Link to="/projects" className="inline-flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-redline-500 text-white"><HardHat className="h-4 w-4" /></span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-ink">Open work board</span>
        </Link>
        <Link to="/vendor" className="text-xs font-bold text-ink/50 hover:text-ink" data-testid="pb-vendor-login">
          Already registered? Sign in
        </Link>
      </div>
    </div>
    {children}
  </div>
);

/* ── register to bid ───────────────────────────────────────────────────────── */
function Register({ ident, category, bidFirst, onDone }) {
  const [f, setF] = useState({ company: "", contact_name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  if (done) return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="pb-registered">
      <CheckCircle2 className="h-7 w-7 text-emerald-600" />
      <h3 className="mt-2 font-heading text-xl font-extrabold text-ink">You're registered to bid</h3>
      <p className="mt-1 text-sm leading-relaxed text-ink/65">
        The job is at <b>{done.community}</b>. We've texted and emailed your own private page — that's
        where you put your price in, ask questions and book a walk-through.
      </p>
      <p className="mt-2 text-xs leading-relaxed text-ink/55" data-testid="pb-paperwork-note">
        {bidFirst
          ? "Coming out to look at the job needs nothing from you, and you can send your price straight away. Before you can start the work we'll need your certificate of insurance, your W-9 and a signed agreement — all three upload on that page."
          : "Coming out to look at the job needs nothing from you. Before you can submit a price we'll need your certificate of insurance, your W-9 and a signed agreement — all three upload on that page."}
      </p>
      <a href={done.portal_url} data-testid="pb-open-portal"
        className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-ink px-5 py-2.5 text-sm font-bold text-white">
        Open my page <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );

  return (
    <div className="rounded-2xl border border-sand-200 bg-white p-6" data-testid="pb-register">
      <h3 className="font-heading text-xl font-extrabold text-ink">Want to bid on this?</h3>
      <p className="mt-1 text-sm leading-relaxed text-ink/60">
        Tell us who you are and we'll hand you the community's name, the full scope and your own bidder
        page. No account, no password.
      </p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <input data-testid="pb-company" value={f.company} placeholder="Company name *"
          onChange={(e) => setF({ ...f, company: e.target.value })}
          className="rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
        <input data-testid="pb-contact" value={f.contact_name} placeholder="Your name"
          onChange={(e) => setF({ ...f, contact_name: e.target.value })}
          className="rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
        <input data-testid="pb-phone" value={f.phone} placeholder="Mobile number"
          onChange={(e) => setF({ ...f, phone: e.target.value })}
          className="rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
        <input data-testid="pb-email" value={f.email} placeholder="Email"
          onChange={(e) => setF({ ...f, email: e.target.value })}
          className="rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
      </div>
      <button data-testid="pb-register-submit" disabled={busy || f.company.trim().length < 2}
        onClick={async () => {
          setBusy(true);
          try {
            const { data } = await api.post(`/fs/public/projects/${ident}/register-intent`,
              { ...f, trades: category ? [category] : [] });
            setDone(data); onDone?.(data);
          } catch (e) { toast.error(errMsg(e, "Try again")); }
          setBusy(false);
        }}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white hover:bg-redline-600 disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Register to bid
      </button>
      <p className="mt-2 text-center text-[11px] text-ink/40">
        We'll text and email you the link. Message and data rates may apply; reply STOP to opt out.
      </p>
    </div>
  );
}

/* ── one listing ──────────────────────────────────────────────────────────── */
export function PublicProject() {
  const { ident } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/fs/public/projects/${ident}`).then(({ data }) => setP(data)).catch(() => setP({ error: true }));
  }, [ident]);

  if (!p) return <Shell><div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div></Shell>;
  if (p.error) return (
    <Shell>
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <p className="text-base text-ink/60">That listing isn't available any more.</p>
        <Link to="/projects" className="mt-3 inline-block text-sm font-bold text-redline-600 underline">See what's open</Link>
      </div>
    </Shell>
  );
  const dl = daysLeft(p.bids_due);

  return (
    <Shell>
      <div className="mx-auto max-w-3xl px-6 py-10" data-testid="pb-project">
        <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wider text-redline-600">
          <span>{CAT_LABEL[p.category] || "General work"}</span>
          {p.market && <span className="inline-flex items-center gap-1 text-ink/40"><MapPin className="h-3 w-3" /> {p.market}</span>}
        </div>
        <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">{p.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-ink/55">
          <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-ink/30" /> {p.community}</span>
          {p.bids_due && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="h-4 w-4 text-ink/30" />
              {p.closed ? `Closed ${String(p.bids_due).slice(0, 10)}`
                : dl === 0 ? "Bids close today" : `Bids close in ${dl} day${dl === 1 ? "" : "s"}`}
            </span>
          )}
          {p.bids_in > 0 && <span className="text-ink/40">{p.bids_in} bid(s) in</span>}
        </div>

        {p.description && <p className="mt-6 whitespace-pre-line text-base leading-relaxed text-ink/75">{p.description}</p>}
        {p.scope && (
          <div className="mt-6">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Scope of work</div>
            <p className="whitespace-pre-line rounded-2xl bg-white px-5 py-4 text-sm leading-relaxed text-ink/75">{p.scope}</p>
          </div>
        )}
        {(p.attachments || 0) > 0 && (
          <p className="mt-3 text-xs text-ink/50">{p.attachments} document(s) attached — you'll see them once you register.</p>
        )}

        {(p.questions || []).length > 0 && (
          <div className="mt-8" data-testid="pb-questions">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/45">Questions everyone's been asked</div>
            {p.questions.map((q, i) => (
              <div key={i} className="mb-2 rounded-2xl bg-white px-5 py-4">
                <div className="text-sm font-bold text-ink">{q.q}</div>
                <div className="mt-1 text-sm text-ink/70">{q.a}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8">
          {p.closed ? (
            <div className="rounded-2xl bg-sand-100 px-5 py-4 text-sm text-ink/60" data-testid="pb-closed">
              Bidding on this one has closed. <Link to="/projects" className="font-bold underline">See what else is open.</Link>
            </div>
          ) : (
            <Register ident={p.token || p.id} category={p.category}
              bidFirst={p.bid_without_paperwork !== false} />
          )}
        </div>

        <div className="mt-8 flex items-start gap-2 rounded-2xl border border-sand-200 bg-white px-5 py-4">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-ink/35" />
          <p className="text-xs leading-relaxed text-ink/55">
            Every community on Redline verifies insurance before a company is given work. You keep one
            certificate on file and it covers every property you serve — you never apply twice.
          </p>
        </div>
      </div>
    </Shell>
  );
}

/* ── the board ────────────────────────────────────────────────────────────── */
export default function PublicProjects() {
  const [d, setD] = useState(null);
  const [trade, setTrade] = useState("");
  const [market, setMarket] = useState("");
  const nav = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/fs/public/projects", { params: { trade, market } });
    setD(data);
  }, [trade, market]);
  useEffect(() => { load().catch(() => setD({ projects: [], closed: [] })); }, [load]);

  return (
    <Shell>
      <div className="mx-auto max-w-5xl px-6 py-10" data-testid="public-projects">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
          Work that needs a price
        </h1>
        <p className="mt-2 max-w-2xl text-base leading-relaxed text-ink/60">
          Real jobs at communities managed on Redline. Pick one, register in thirty seconds, and you'll
          get the community's name, the full scope and your own page to bid from. Coming out to look at
          the job costs you no paperwork.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <select data-testid="pb-trade" value={trade} onChange={(e) => setTrade(e.target.value)}
            className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm font-semibold text-ink/70 outline-none">
            <option value="">Every trade</option>
            {Object.entries(CAT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/30" />
            <input data-testid="pb-market" value={market} onChange={(e) => setMarket(e.target.value)}
              placeholder="City or area"
              className="w-48 rounded-xl border border-sand-300 py-2 pl-8 pr-3 text-sm outline-none" />
          </div>
          {(d?.markets || []).slice(0, 6).map((m) => (
            <button key={m} onClick={() => setMarket(m)}
              className="rounded-full bg-sand-100 px-3 py-1.5 text-xs font-bold text-ink/55 hover:bg-sand-200">{m}</button>
          ))}
        </div>

        {!d ? (
          <div className="grid h-48 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>
        ) : !(d.projects || []).length ? (
          <div className="mt-8 rounded-2xl border border-dashed border-sand-300 bg-white py-16 text-center" data-testid="pb-empty">
            <HardHat className="mx-auto h-8 w-8 text-ink/20" />
            <p className="mt-3 text-base font-semibold text-ink/60">Nothing open right now</p>
            <p className="mx-auto mt-1 max-w-md text-sm leading-relaxed text-ink/45">
              New work is posted here every week. Register once and we'll text you the moment something
              in your trade and your area comes up.
            </p>
            <Link to="/vendor-apply" data-testid="pb-apply"
              className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-ink px-5 py-2.5 text-sm font-bold text-white">
              Join the network <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {d.projects.map((p) => {
              const dl = daysLeft(p.bids_due);
              return (
                <button key={p.id} data-testid={`pb-card-${p.id}`} onClick={() => nav(`/projects/${p.token || p.id}`)}
                  className="rounded-2xl border border-sand-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-sand-300 hover:shadow-sm">
                  <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-redline-600">
                    <span>{CAT_LABEL[p.category] || "General"}</span>
                    {p.market && <span className="text-ink/35">{p.market}</span>}
                  </div>
                  <div className="mt-1.5 font-heading text-lg font-bold leading-snug text-ink">{p.title}</div>
                  <div className="mt-1 text-xs text-ink/45">{p.community}</div>
                  {p.scope && <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink/60">{p.scope}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-semibold text-ink/50">
                    {p.bids_due && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5" />
                        {dl === 0 ? "closes today" : `${dl} day${dl === 1 ? "" : "s"} left`}
                      </span>
                    )}
                    {p.bids_in > 0 && <span>{p.bids_in} bid(s) in</span>}
                    <span className="ml-auto inline-flex items-center gap-1 text-redline-600">
                      Bid on it <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {(d?.closed || []).length > 0 && (
          <div className="mt-10" data-testid="pb-closed-list">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Recently closed</div>
            {d.closed.map((p) => (
              <div key={p.id} className="mb-1.5 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white px-4 py-2.5 text-sm">
                <span className="text-ink/55">{p.title} · {p.community}</span>
                <span className="text-xs text-ink/35">{p.bids_in} bid(s)</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
